import React, { useState, useEffect, useRef } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import TextInput from 'components/Common/Form/Input/TextInput'
import IconButton from 'components/Luxkit/Button/IconButton'
import SolidMessageIcon from 'components/Luxkit/Icons/solid/SolidMessageIcon'
import { ChatStatuses } from 'supportAssistant/constants/chatStatuses'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import { ChatAgentTypes } from '../../constants/chatAgentTypes'

const TextInputWithSpace = styled(TextInput)`
  input {
    padding-right: ${rem(42)};
  }

  > * {
    margin-bottom: 0;
  }

  span[class^='InputWrap__StyledInputWrap'] {
    border-radius: ${props => props.theme.borderRadius.S};
    height: ${rem(48)};
    // margin-top: ${rem(4)};
  }
`

const SendButton = styled(IconButton)`
  position: absolute;
  right: ${rem(4)};
  top: 50%;
  transform: translateY(-50%);
`

interface Props {
  chatStatus?: ChatStatuses;
  chatAgentType: ChatAgentTypes;
  error?: string;
  onMessageSend: (message: string) => void;
  isTyping: boolean;
  isLastMessageByUser: boolean;
  isDown?: boolean;
}

function MessageInput({
  chatStatus,
  chatAgentType,
  error,
  onMessageSend,
  isTyping,
  isLastMessageByUser,
  isDown,
}: Props) {
  const [message, setMessage] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const isMobile = useIsMobileScreen()

  const isInputButtonDisabled = chatStatus === ChatStatuses.CLOSED || !message || isTyping || isDown || (chatAgentType === ChatAgentTypes.BOT && isLastMessageByUser)
  const isInputDisabled = chatStatus === ChatStatuses.CLOSED || isDown

  useEffect(() => {
    if (!message && !isMobile) {
      inputRef.current?.focus({
        preventScroll: true,
      })
    }
  }, [isMobile, message])

  const onMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value)
  }

  const onKeyEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isEnter = event.type === 'keyup' && event.key === 'Enter'

    if (isEnter && !isInputButtonDisabled) {
      onMessageSend(message)
      setMessage('')
    }
  }

  const onMessageSubmit = () => {
    onMessageSend(message)
    setMessage('')
  }

  return (
    <TextInputWithSpace
      type="text"
      maxLength={250}
      disabled={isInputDisabled}
      name="Message"
      aria-label="Message"
      placeholder="Type a message"
      onChange={onMessageChange}
      value={message}
      ref={inputRef}
      onKeyUp={onKeyEnter}
      invalidErrorMessage={error}
      manualError
      helpText={message.length >= 250 ? 'Messages are limited to 250 characters' : ''}
    >
      <SendButton
        kind="tertiary"
        variant="dark"
        onClick={onMessageSubmit}
        disabled={isInputButtonDisabled}
      >
        <SolidMessageIcon />
      </SendButton>
    </TextInputWithSpace>
  )
}

export default MessageInput
